/**
 * Desktop sidebar icon-rail collapse preference (#2579, EPIC #2575 Tier 4).
 *
 * A single shared hook so BOTH the agent CRM sidebar (CrmSidebar) and the admin
 * sidebar (AdminSidebar) — plus their layout content offsets — read/write the
 * SAME persisted preference. When collapsed the desktop (`lg:`+) sidebar shrinks
 * from `w-64` to a `w-16` icon rail; the content area's left offset animates in
 * lockstep. Mobile keeps its off-canvas drawer untouched (this preference has no
 * effect below `lg:`).
 *
 * Persistence follows the `useTheme` STORAGE_KEY pattern exactly: a single
 * localStorage key, hydrated once on mount, written on every change. This is a
 * DELIBERATE cross-session preference and is intentionally distinct from the
 * session-only, unpersisted nav-group accordion expand state (#803) — collapsing
 * the whole rail is a different, sticky choice.
 *
 * The state is shared across every component instance in a render tree via a
 * tiny module-level subscriber set (useSyncExternalStore), so toggling the rail
 * from one sidebar updates the layout offset in the same tick without prop
 * drilling or a context provider.
 */
import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "crm-sidebar-collapsed";

function read(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "true";
}

let collapsed = read();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  // Reconcile with persisted state on (re)subscribe so a fresh mount — a page
  // reload, a new tab, or another tab having changed the preference — always
  // reflects what is in localStorage, not a stale module snapshot. Mirrors the
  // reload-faithfulness of the useTheme hydration.
  const persisted = read();
  if (persisted !== collapsed) {
    collapsed = persisted;
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return collapsed;
}

function getServerSnapshot(): boolean {
  return false;
}

function setCollapsed(next: boolean) {
  collapsed = next;
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, String(next));
  }
  emit();
}

export function useSidebarCollapsed() {
  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, []);

  const setSidebarCollapsed = useCallback((next: boolean) => {
    setCollapsed(next);
  }, []);

  return { collapsed: value, toggleCollapsed, setSidebarCollapsed };
}

export const SIDEBAR_COLLAPSED_STORAGE_KEY = STORAGE_KEY;
